import { CardPaymentGroupActions, CardPaymentMethodData, CardPaymentMethodMeta } from "./Card";
import { CodPaymentMethodData, TURBO_COD_CONFIRM_HALF_CARD_TYPE, TurboCodPaymentMethodMeta } from "./Cod";
import { InAppUpiPaymentGroupActions, InAppUpiPaymentMethod, InAppUpiPaymentMethodMeta } from "./InAppUpi";
import { PaymentGroupActions } from "./Payment";
import {
    SwiggyInstrumentsMeta,
    SwiggyInstrumentsPaymentGroupActions,
    SwiggyInstrumentsPaymentMethodData,
} from "./SwiggyInstruments";
import { UpiPaymentGroupActions, UpiPaymentMethod, UpiPaymentMethodMeta } from "./Upi";
import { WalletPaymentGroupActions, WalletPaymentMethodData, WalletPaymentMethodMeta } from "./Wallet";

import { CredEligibleData } from "./CredPay";
//internal-deps
import { CartContextForAnalytics } from "@swiggy-private/payment-types";
import { ITypography } from "@swiggy-private/dls-web-common/dist/components/types";
import { B2CPaymentMethodData } from "./BillToCompany";
import { TurboStripEventAction, TurboStripData } from "@swiggy-private/core-ui";
import { IColorMode } from "@swiggy-private/dls-web-common/dist/components/types";

export type TurboPaymentSpecificActions =
    | (
          | UpiPaymentGroupActions
          | CardPaymentGroupActions
          | WalletPaymentGroupActions
          | SwiggyInstrumentsPaymentGroupActions
          | InAppUpiPaymentGroupActions
          | PaymentGroupActions<CodPaymentMethodData>
      )
    | undefined;
export type TurboPaymentMethodMeta =
    | UpiPaymentMethodMeta
    | CardPaymentMethodMeta
    | WalletPaymentMethodMeta
    | SwiggyInstrumentsMeta
    | TurboCodPaymentMethodMeta
    | InAppUpiPaymentMethodMeta;

export type TurboPaymentMethod =
    | UpiPaymentMethod
    | CardPaymentMethodData
    | WalletPaymentMethodData
    | SwiggyInstrumentsPaymentMethodData
    | CodPaymentMethodData
    | InAppUpiPaymentMethod
    | B2CPaymentMethodData;

export type TurboAutoPayMethod = CodPaymentMethodData;

export interface PaymentMethodDowntime {
    singleClickPresentationInfo: {
        title: string;
        subtitle: string;
        icon: string;
    };
    paymentPageHalfcardPlaceholders: {
        paymentMethodDisplayName: string;
    };
}

export type TurboPayOfferComms = {
    showTooltip: boolean;
    toolTipText: string;
};

export interface TurboCommsConfig {
    text: string;
    commsDataTestId: string;
    textVariant: keyof ITypography;
}

export enum TurboPaymentCommsType {
    EDU_COMMS = "eduComms",
    OFFER_COMMS = "offerComms",
}

/**
 * Layout/behaviour family for the turbo widget. `DEFAULT` is the existing turbo surface and must
 * stay a no-op passthrough; every new opt-in surface gets its own member instead of a new boolean.
 */
export enum TurboPaymentVariant {
    DEFAULT = "DEFAULT",
    QUICK_CHECKOUT = "QUICK_CHECKOUT",
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SharedTurboVariantData {
    // add common or shared props here
}

/** Caller-owned inputs that only the quick-checkout turbo layout needs. */
export interface QuickCheckoutTurboVariantData extends SharedTurboVariantData {
    totalItemCount?: number;
    cartContextForAnalytics?: CartContextForAnalytics;
}

/**
 * Data contract per variant. `DEFAULT` carries nothing, so existing callers pass neither prop.
 * When a second non-default variant lands, add its shape to this union and narrow on `variant`
 * before reading it.
 */
export type TurboVariantData = QuickCheckoutTurboVariantData;

export interface TurboVariantProps {
    variant?: TurboPaymentVariant;
    /** Only read when it matches the active `variant`; ignored on `DEFAULT`. */
    variantData?: TurboVariantData;
}

export interface TurboPodHalfCardEvents {
    onLoad: (type: TURBO_COD_CONFIRM_HALF_CARD_TYPE) => void;
    onPlaceOrderCtaClick: (flow: TURBO_COD_CONFIRM_HALF_CARD_TYPE, isSliderCompleted?: "yes" | "no") => void;
    onCancelCtaClick: (flow: TURBO_COD_CONFIRM_HALF_CARD_TYPE) => void;
}

export interface ITurboPaymentMethodProps {
    methodData: TurboPaymentMethod;
    paymentActions: PaymentGroupActions<TurboPaymentMethod>;
    onClickChangePaymentCta: VoidFunction;
    turboPaymentCtaMeta?: {
        amountWithDiscount: string;
        amountWithoutDiscount?: string;
        mainText: string;
        subText: string;
    };
    showDiscountedAmount: boolean;
    balanceValue?: string | number; // for wallets + swiggy-money + upi lite
    credEligibleData?: CredEligibleData; // for cred
    turboPodHalfCardEvents: TurboPodHalfCardEvents;
    increaseSliderWidth: boolean;
    isTurboV2?: boolean;
    isPopCart?: boolean;
    onClickTurboStrip?: TurboStripEventAction;
    onImpressionTurboStrip?: TurboStripEventAction;
    turboStrip?: TurboStripData;
    isBillToCompanyExist?: boolean;
    clampDiscountText?: boolean;
    /** This is to handle the color only for scenes mode for the turbo payment */
    forceColorMode?: IColorMode;
    onCvvPopupOpen?: VoidFunction;
    keyFactStatementUrl?: string;
    onKeyFactStatementClick?: (url: string) => void;
}

/**
 * Quick-checkout turbo method widget props: the BAU shape minus everything AIP does not render
 * (turbo strip, POD half-card, bill-to-company, KFS, CVV bottom sheet), plus the inline loading
 * state it shows in place of a shimmer. Turbo-v2 is implied by this widget, so it is not a prop.
 */
export interface IQuickCheckoutTurboPaymentMethodProps extends Omit<
    ITurboPaymentMethodProps,
    | "turboPodHalfCardEvents"
    | "isTurboV2"
    | "isPopCart"
    | "onClickTurboStrip"
    | "onImpressionTurboStrip"
    | "turboStrip"
    | "isBillToCompanyExist"
    | "keyFactStatementUrl"
    | "onKeyFactStatementClick"
    | "onCvvPopupOpen"
> {
    isLoading?: boolean;
    isLoadingForTheFirstTime?: boolean;
    totalItemCount?: number;
}
